import { BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSidebarSafe } from "@/lib/context/sidebar-context";
import { useStandaloneMode } from "@/lib/hooks/use-standalone-mode";
import { cn } from "@/lib/utils";

type DocButtonProps = {
  /** The slug or pageId of the documentation page to open */
  docSlug: string;
  /** Optional button variant */
  variant?: "default" | "outline" | "ghost" | "link";
  /** Optional button size */
  size?: "default" | "sm" | "lg" | "icon";
  /** Optional className for additional styling */
  className?: string;
  /** Optional children to override default icon */
  children?: React.ReactNode;
  /** Hover hint. Also the accessible name when the button is icon-only. */
  title?: string;
};

/**
 * Reusable button component that opens the documentation sidebar
 * to a specific documentation page.
 *
 * @example
 * // Icon button
 * <DocButton docSlug="flows" size="icon" variant="ghost" />
 *
 * @example
 * // Button with text
 * <DocButton docSlug="creating-flows" variant="outline">
 *   View Documentation
 * </DocButton>
 */
export function DocButton({
  docSlug,
  variant = "ghost",
  size = "icon",
  className,
  children,
  title = "View documentation",
}: DocButtonProps) {
  const sidebar = useSidebarSafe();
  const { standalone } = useStandaloneMode();

  // Don't render if sidebar context is not available (e.g., during SSG), or in
  // QL-33 standalone mode — the docs sidebar the button opens is not mounted.
  if (!sidebar || standalone) {
    return null;
  }

  // Split on the size rather than passing it through: `tooltip` is required on
  // size="icon" and optional otherwise, and a union-typed `size` matches neither
  // half of that. The two branches are otherwise identical.
  const shared = {
    variant,
    onClick: () => sidebar.openDocPage(docSlug),
    className: cn("shrink-0", className),
    tooltip: title,
    type: "button" as const,
    children: children || <BookOpen className="h-4 w-4" />,
  };

  return size === "icon" ? <Button {...shared} size="icon" /> : <Button {...shared} size={size} />;
}
